import invariant from 'tiny-invariant';

import { fieldConfigurationsRaw } from '@packages/query-broker/fields/get-field-configurations';

export const getFieldData = (
  field: string,
): {
  type: string;
  items?: {
    type: string;
  };
} => {
  const data =
    fieldConfigurationsRaw[field as keyof typeof fieldConfigurationsRaw];
  invariant(data, `field: ${field} does not exist`);
  return data;
};

const isStringType = (type: string) => {
  return type === 'string' || type === 'ip' || type === 'uuid';
};

export const isStringField = (field: string) => {
  const { type, items } = getFieldData(field);
  return (
    isStringType(type) ||
    (type === 'array' && items ? isStringType(items.type) : false)
  );
};

const isNumericType = (type: string) => {
  return (
    type === 'int64' ||
    type === 'number' ||
    type === 'uint8' ||
    type === 'uint32' ||
    type === 'uint16' ||
    type === 'integer' ||
    type === 'date-time'
  );
};

export const isNumericField = (field: string) => {
  const { type, items } = getFieldData(field);
  return (
    isNumericType(type) ||
    (type === 'array' && items ? isNumericType(items.type) : false)
  );
};

const maxValByTypeMap: Record<string, number | bigint> = {
  uint8: Math.pow(2, 8) - 1,
  uint16: Math.pow(2, 16) - 1,
  uint32: Math.pow(2, 32) - 1,
  int64: 9223372036854775807n,
};

const isInt64 = (num: bigint) => {
  if (num < maxValByTypeMap.int64 && num > -9223372036854775808n) return true;
  return false;
};

type NumberValidatorResponse = {
  isOverflowing: boolean;
  maxVal: number | bigint;
  minVal: number | bigint;
};

const numberValidatorByTypeMap: Record<
  string,
  (val: string) => NumberValidatorResponse
> = {
  uint8: (val: string) => {
    const numVal = Number(val);
    return {
      isOverflowing: numVal > maxValByTypeMap.uint8,
      maxVal: maxValByTypeMap.uint8,
      minVal: 0,
    };
  },
  uint16: (val: string) => {
    const numVal = Number(val);
    return {
      isOverflowing: numVal > maxValByTypeMap.uint16,
      maxVal: maxValByTypeMap.uint16,
      minVal: 0,
    };
  },
  uint32: (val: string) => {
    const numVal = Number(val);
    return {
      isOverflowing: numVal > maxValByTypeMap.uint32,
      maxVal: maxValByTypeMap.uint32,
      minVal: 0,
    };
  },
  int64: (val: string) => {
    const numVal = BigInt(val);
    return {
      isOverflowing: !isInt64(numVal),
      maxVal: maxValByTypeMap.int64,
      minVal: -9223372036854775808n,
    };
  },
};

export const getMaxValComparison = (field: string, val: string) => {
  const { type } = getFieldData(field);
  const validator = numberValidatorByTypeMap[type];

  if (!validator) {
    return { isOverflowing: false, maxVal: 0, minVal: 0 };
  }

  return validator(val);
};
